import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import {
  campaignAPI,
  contactListAPI,
  tenantAPI,
  ContactList,
} from '../services/api';
import ViberMessageBuilder from '../components/ViberMessageBuilder';
import ViberMessagePreview from '../components/ViberMessagePreview';
import FallbackSelector from '../components/FallbackSelector';
import { ViberMessageOptions } from '../types/viber.types';
import Icon, { type IconName } from '../components/Icon';
import Pagination from '../components/Pagination';
import { CHANNEL_META, channelVars, type Channel } from '../utils/channel';
import { fmt } from '../utils/format';
import { useT } from '../i18n/LanguageContext';

const channelIdOf = (ch: Channel): number => (ch === 'sms' ? 2 : ch === 'viber' ? 3 : 1);

const CampaignWizard: React.FC = () => {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const STEP_LABELS = [t('Kanal', 'Channel'), t('Sadržaj', 'Content'), t('Primaoci', 'Recipients'), t('Pregled', 'Review')];

  const CHANNEL_CARDS: { ch: Channel; icon: IconName; title: string; desc: string }[] = [
    { ch: 'email', icon: 'mail', title: 'Email', desc: t('HTML newsletteri sa personalizacijom i praćenjem.', 'HTML newsletters with personalization and tracking.') },
    { ch: 'sms', icon: 'sms', title: 'SMS', desc: t('Kratke direktne poruke. Najveća stopa čitanja.', 'Short direct messages. Highest read rate.') },
    { ch: 'viber', icon: 'viber', title: 'Viber', desc: t('Rich poruke sa slikom i dugmetom + SMS rezerva.', 'Rich messages with image and button + SMS fallback.') },
  ];

  // pre-selected channel from query (?channel=) or from edit-load
  const preChannel = (searchParams.get('channel') as Channel | null) || null;

  const [step, setStep] = useState(preChannel ? 1 : 0);
  const [channel, setChannel] = useState<Channel | null>(preChannel);

  // shared
  const [name, setName] = useState('');
  const [availableLists, setAvailableLists] = useState<ContactList[]>([]);
  const [selectedLists, setSelectedLists] = useState<number[]>([]);
  // Audience step: search + pagination over the lists (selection is kept in
  // `selectedLists`, so filtering/paging never clears what's already checked).
  const [listSearch, setListSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 10;

  // email
  const [subject, setSubject] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [designJson, setDesignJson] = useState<string | undefined>(undefined);
  const [useHtmlEditor, setUseHtmlEditor] = useState(false);
  const [tenantSender, setTenantSender] = useState<{ fromEmail: string | null; fromName: string | null; configured: boolean } | null>(null);
  const [allowedChannels, setAllowedChannels] = useState<{ email: boolean; sms: boolean; viber: boolean }>({ email: true, sms: true, viber: true });
  const emailEditorRef = useRef<EditorRef>(null);
  const [editorReady, setEditorReady] = useState(false);

  // sms
  const [smsText, setSmsText] = useState('');

  // viber
  const [viberContent, setViberContent] = useState<ViberMessageOptions>({ text: '', label: 'PROMOTION', ttl: 86400 });
  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [fallbackSmsContent, setFallbackSmsContent] = useState('');

  // scheduling
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [scheduledAt, setScheduledAt] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadLists();
    loadSender();
    if (isEdit) loadCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Once tenant capabilities load, if a NEW campaign was opened on a channel the
  // tenant isn't allowed to use (e.g. via ?channel=viber direct URL), bounce
  // back to the channel picker. Editing existing campaigns is not blocked.
  useEffect(() => {
    if (!isEdit && channel && !allowedChannels[channel]) {
      setChannel(null);
      setStep(0);
      setError(t('Taj kanal nije omogućen za vašeg klijenta.', 'That channel is not enabled for your account.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedChannels]);

  const loadLists = async () => {
    try {
      const res = await contactListAPI.getAll({ limit: 1000 });
      if (res.data.success) setAvailableLists(res.data.data.lists);
    } catch (e) {
      console.error('Error loading lists:', e);
    }
  };

  const loadSender = async () => {
    try {
      const res = await tenantAPI.getMySender();
      if (res.data.success) {
        setTenantSender(res.data.data);
        if (res.data.data.channels) setAllowedChannels(res.data.data.channels);
        if (res.data.data.fromEmail) setFromEmail((prev) => prev || res.data.data.fromEmail!);
      }
    } catch (e) {
      console.error('Error loading tenant sender:', e);
    }
  };

  const loadCampaign = async () => {
    try {
      const res = await campaignAPI.getOne(parseInt(id!));
      if (!res.data.success) return;
      const c = res.data.data.campaign;
      const ch: Channel = c.channelId === 2 ? 'sms' : c.channelId === 3 || c.viberRichContent ? 'viber' : 'email';
      setChannel(ch);
      setStep(1);
      setName(c.name || '');

      if (ch === 'email') {
        setSubject(c.subject || '');
        setFromEmail(c.fromEmail || '');
        setHtmlContent(c.htmlContent || '');
        setDesignJson(c.designJson);
        if (!c.designJson) setUseHtmlEditor(true);
      } else {
        // sms + viber both stored as rich content
        if (c.viberRichContent) {
          const rc = JSON.parse(c.viberRichContent);
          setViberContent({
            text: rc.text || '',
            label: rc.label || 'PROMOTION',
            button: rc.buttonCaption ? { caption: rc.buttonCaption, action: rc.buttonAction } : undefined,
            image: rc.image,
            video: rc.video,
            carousel: rc.carousel,
            tag: rc.tag,
            ttl: rc.ttl || 86400,
            priority: rc.priority,
            smartphoneOnly: rc.smartphoneOnly,
          });
          if (ch === 'sms') setSmsText(rc.text || '');
        }
        setFallbackEnabled(c.fallbackEnabled || false);
        setFallbackSmsContent(c.fallbackSmsContent || '');
      }

      try {
        const listsRes = await campaignAPI.getLists(parseInt(id!));
        if (listsRes.data.success) setSelectedLists(listsRes.data.data.lists.map((l) => l.id));
      } catch {
        /* ignore */
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  // Load saved Unlayer design once the editor reports ready.
  useEffect(() => {
    if (editorReady && designJson && emailEditorRef.current?.editor) {
      try {
        emailEditorRef.current.editor.loadDesign(JSON.parse(designJson));
      } catch (e) {
        console.error('Failed to load saved design:', e);
      }
    }
  }, [editorReady, designJson]);

  const onEditorReady: EmailEditorProps['onReady'] = () => setEditorReady(true);

  const exportFromEditor = (): Promise<{ html: string; designJson: string }> =>
    new Promise((resolve, reject) => {
      const unlayer = emailEditorRef.current?.editor;
      if (!unlayer) return reject(new Error(t('Editor nije spreman', 'Editor is not ready')));
      unlayer.exportHtml((data: any) => resolve({ html: data.html, designJson: JSON.stringify(data.design) }));
    });

  // Advance a step. When leaving the Content step on an email campaign built in
  // the drag-and-drop editor, snapshot the HTML/design first — the editor
  // unmounts on later steps, so this cache is what gets persisted at the end.
  const goNext = async () => {
    if (step === 1 && channel === 'email' && !useHtmlEditor && emailEditorRef.current?.editor) {
      try {
        const exported = await exportFromEditor();
        setHtmlContent(exported.html);
        setDesignJson(exported.designJson);
      } catch (e: any) {
        setError(e.message);
        return;
      }
    }
    setError('');
    setStep((s) => s + 1);
  };

  const toggleList = (listId: number) =>
    setSelectedLists((prev) => (prev.includes(listId) ? prev.filter((x) => x !== listId) : [...prev, listId]));

  const reach = availableLists
    .filter((l) => selectedLists.includes(l.id))
    .reduce((a, l) => a + (l.contactCount || 0), 0);

  // Audience step: filter by name (case-insensitive) then paginate, 10/page.
  const filteredLists = availableLists.filter((l) =>
    l.name.toLowerCase().includes(listSearch.trim().toLowerCase())
  );
  const listTotalPages = Math.max(1, Math.ceil(filteredLists.length / LIST_PAGE_SIZE));
  const listPageSafe = Math.min(listPage, listTotalPages);
  const pagedLists = filteredLists.slice((listPageSafe - 1) * LIST_PAGE_SIZE, listPageSafe * LIST_PAGE_SIZE);

  // step validity gating
  const canNext =
    step === 0
      ? !!channel
      : step === 1
        ? channel === 'email'
          ? !!name.trim() && !!subject.trim()
          : channel === 'sms'
            ? !!name.trim() && !!smsText.trim()
            : !!name.trim() && (!!viberContent.text || !!viberContent.image || !!viberContent.video || !!viberContent.carousel)
        : step === 2
          ? selectedLists.length > 0
          : true;

  const attachAndGenerate = async (campaignId: number) => {
    if (selectedLists.length === 0) return;
    await campaignAPI.attachLists(campaignId, selectedLists);
    await campaignAPI.generateRecipients(campaignId);
  };

  // Create (or update) the campaign and return its id.
  const persistCampaign = async (): Promise<number> => {
    if (channel === 'email') {
      let payload: { name: string; subject: string; fromEmail: string; htmlContent: string; designJson?: string };
      if (useHtmlEditor) {
        if (!htmlContent.trim()) throw new Error(t('HTML sadržaj je obavezan', 'HTML content is required'));
        payload = { name, subject, fromEmail, htmlContent, designJson };
      } else {
        // The Unlayer editor only lives on the Content step, so on Review it is
        // unmounted. Export live when it's still mounted, otherwise fall back to
        // the html/design we cached when leaving the Content step (see goNext).
        let html = htmlContent;
        let dj = designJson;
        if (emailEditorRef.current?.editor) {
          const exported = await exportFromEditor();
          html = exported.html;
          dj = exported.designJson;
        }
        if (!html.trim()) throw new Error(t('Sadržaj email-a je prazan — vrati se na korak „Sadržaj".', 'Email content is empty — go back to the "Content" step.'));
        payload = { name, subject, fromEmail, htmlContent: html, designJson: dj };
      }
      if (isEdit) {
        await campaignAPI.update(parseInt(id!), payload);
        await attachAndGenerate(parseInt(id!));
        return parseInt(id!);
      }
      const res = await campaignAPI.create(payload);
      const newId = res.data.data.id;
      await attachAndGenerate(newId);
      return newId;
    }

    // sms + viber -> rich-content endpoint (channelId distinguishes them)
    const content: ViberMessageOptions = channel === 'sms' ? { text: smsText, label: 'PROMOTION', ttl: 86400 } : viberContent;
    if (isEdit) {
      await campaignAPI.updateViberContent(parseInt(id!), { viberContent: content, fallbackEnabled, fallbackSmsContent });
      await attachAndGenerate(parseInt(id!));
      return parseInt(id!);
    }
    const res = await campaignAPI.createViberCampaign({
      name,
      channelId: channelIdOf(channel!),
      viberContent: content,
      fallbackEnabled,
      fallbackSmsContent,
    });
    const newId = res.data.data.campaign.id;
    await attachAndGenerate(newId);
    return newId;
  };

  const saveDraft = async () => {
    setLoading(true);
    setError('');
    try {
      const campaignId = await persistCampaign();
      setSuccess(t('Nacrt sačuvan.', 'Draft saved.'));
      setTimeout(() => navigate(`/campaigns/${campaignId}`), 800);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const campaignId = await persistCampaign();
      if (when === 'now') {
        await campaignAPI.start(campaignId);
        setSuccess(t('Kampanja je pokrenuta!', 'Campaign has been started!'));
      } else {
        if (!scheduledAt) throw new Error(t('Izaberi datum i vreme za zakazivanje.', 'Select a date and time to schedule.'));
        await campaignAPI.scheduleCampaign(campaignId, { scheduledAt: new Date(scheduledAt).toISOString() });
        setSuccess(t('Kampanja je zakazana!', 'Campaign has been scheduled!'));
      }
      setTimeout(() => navigate(`/campaigns/${campaignId}`), 1000);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Back / Next (or Finish on the last step). Rendered both at the top of the
  // wizard and at the bottom so navigation is reachable without scrolling.
  const navButtons = (
    <div className="flex items-center justify-between">
      <button className="btn btn-secondary" disabled={step === 0 || loading} onClick={() => setStep((s) => Math.max(0, s - 1))}>
        <Icon name="chevronLeft" size={16} /> {t('Nazad', 'Back')}
      </button>
      {step < 3 ? (
        <button className="btn btn-primary" disabled={!canNext} onClick={goNext}>
          {t('Dalje', 'Next')} <Icon name="arrowRight" size={16} />
        </button>
      ) : (
        <button className="btn btn-primary btn-lg" disabled={loading} onClick={finish}>
          <Icon name={when === 'now' ? 'rocket' : 'calendar'} size={17} />
          {loading ? t('Čuvam…', 'Saving…') : when === 'now' ? t('Pokreni slanje', 'Start sending') : t('Zakaži', 'Schedule')}
        </button>
      )}
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 1080 }}>
      {/* header: cancel · steps · save draft */}
      <div className="flex items-center justify-between wrap gap-16" style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" style={{ paddingLeft: 6 }} onClick={() => navigate('/campaigns')}>
          <Icon name="x" size={16} /> {t('Otkaži', 'Cancel')}
        </button>
        <div className="steps">
          {STEP_LABELS.map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <span className={`step-line ${i <= step ? 'done' : ''}`} />}
              <div className={`step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                <span className="step-num">{i < step ? <Icon name="check" size={14} /> : i + 1}</span>
                <span className="step-label">{s}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm" disabled={loading || !channel || !name.trim()} onClick={saveDraft}>
          <Icon name="file" size={15} /> {t('Sačuvaj nacrt', 'Save draft')}
        </button>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 'var(--gap)' }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--ok)', color: 'var(--brand-700)', marginBottom: 'var(--gap)' }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      {/* top nav — same Back/Next as the footer, reachable without scrolling */}
      <div style={{ marginBottom: 'var(--gap)' }}>{navButtons}</div>

      {/* STEP 0 — channel */}
      {step === 0 && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2 style={{ fontSize: 24 }}>{t('Izaberi kanal', 'Choose a channel')}</h2>
            <p className="muted">{t('Kako želiš da dopreš do svoje publike?', 'How do you want to reach your audience?')}</p>
          </div>
          <div className="wiz-channels">
            {CHANNEL_CARDS.filter((o) => allowedChannels[o.ch]).map((o) => {
              const v = channelVars(o.ch);
              const selected = channel === o.ch;
              return (
                <button
                  key={o.ch}
                  onClick={() => setChannel(o.ch)}
                  className="card"
                  style={{ padding: 22, textAlign: 'left', cursor: 'pointer', borderWidth: 2, borderColor: selected ? v.color : 'var(--border)', boxShadow: selected ? 'var(--sh-md)' : 'var(--sh-sm)' }}
                >
                  <span style={{ width: 48, height: 48, borderRadius: 'var(--r-md)', background: v.soft, color: v.color, display: 'grid', placeItems: 'center', marginBottom: 16 }}>
                    <Icon name={o.icon} size={24} />
                  </span>
                  <div style={{ fontWeight: 800, fontSize: 17 }}>{o.title}</div>
                  <p className="muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{o.desc}</p>
                  {selected && (
                    <div style={{ marginTop: 14, color: v.color, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon name="checkCircle" size={16} /> {t('Izabrano', 'Selected')}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 1 — content */}
      {step === 1 && channel && (
        <div className={channel === 'email' ? undefined : 'wiz-content'}>
          <div className="card card-pad">
            <div className="flex items-center gap-12" style={{ marginBottom: 18 }}>
              <span className={`ch-tag ${channel}`}>
                <Icon name={CHANNEL_META[channel].icon} size={14} /> {CHANNEL_META[channel].label}
              </span>
              <span className="dim" style={{ fontSize: 13 }}>{t('Sadržaj poruke', 'Message content')}</span>
            </div>

            <div className="field">
              <label className="label">{t('Naziv kampanje (interno)', 'Campaign name (internal)')}</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('npr. Letnja rasprodaja 2026', 'e.g. Summer sale 2026')} />
            </div>

            {channel === 'email' && (
              <>
                <div className="grid-2">
                  <div className="field">
                    <label className="label">{t('Subject (naslov)', 'Subject (title)')}</label>
                    <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t('Naslov email-a', 'Email subject')} />
                  </div>
                  <div className="field">
                    <label className="label">{t('Pošiljalac', 'Sender')}</label>
                    <input className="input" value={fromEmail} readOnly={!!tenantSender?.configured} onChange={(e) => setFromEmail(e.target.value)} placeholder="pošiljalac@domen.rs" />
                  </div>
                </div>
                {tenantSender && !tenantSender.configured && (
                  <div className="hint" style={{ color: 'var(--warn)' }}>
                    <Icon name="alert" size={13} /> {t('SES pošiljalac nije podešen za tenant — podesi pod Administracija › Klijenti › Kredencijali.', 'SES sender is not configured for the tenant — set it under Administration › Clients › Credentials.')}
                  </div>
                )}

                <div className="field" style={{ marginTop: 8 }}>
                  <div className="flex items-center justify-between" style={{ marginBottom: 7 }}>
                    <label className="label" style={{ margin: 0 }}>{t('Sadržaj email-a', 'Email content')}</label>
                    <label className="flex items-center gap-8" style={{ cursor: 'pointer', userSelect: 'none', fontSize: 13, color: 'var(--text-2)' }}>
                      <input type="checkbox" className="row-check" checked={useHtmlEditor} onChange={(e) => setUseHtmlEditor(e.target.checked)} />
                      {t('HTML kod', 'HTML code')}
                    </label>
                  </div>
                  {useHtmlEditor ? (
                    <>
                      <textarea
                        className="textarea mono"
                        value={htmlContent}
                        onChange={(e) => setHtmlContent(e.target.value)}
                        style={{ minHeight: 420 }}
                        placeholder={'<html>\n  <body>\n    <h1>Zdravo {{name}}!</h1>\n  </body>\n</html>'}
                      />
                      <div className="hint">{t('Sirov HTML. Personalizacija:', 'Raw HTML. Personalization:')} {'{{name}}'}, {'{{email}}'}, {'{{unsubscribe_url}}'} {t('i custom polja iz Excel-a.', 'and custom fields from Excel.')}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden', minHeight: 600 }}>
                        <EmailEditor
                          ref={emailEditorRef}
                          onReady={onEditorReady}
                          minHeight="600px"
                          options={{
                            displayMode: 'email',
                            mergeTags: {
                              name: { name: t('Ime kontakta', 'Contact name'), value: '{{name}}', sample: 'Marko' },
                              email: { name: t('Email kontakta', 'Contact email'), value: '{{email}}', sample: 'marko@example.com' },
                              unsubscribe_url: { name: t('Unsubscribe link', 'Unsubscribe link'), value: '{{unsubscribe_url}}', sample: 'https://...' },
                            },
                          }}
                        />
                      </div>
                      <div className="hint"><Icon name="info" size={13} /> {t('Drag-and-drop editor (Unlayer). „Merge tag" dropdown ubacuje promenljive.', 'Drag-and-drop editor (Unlayer). The "Merge tag" dropdown inserts variables.')}</div>
                    </>
                  )}
                </div>
              </>
            )}

            {channel === 'sms' && (
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label">{t('Poruka', 'Message')}</label>
                <textarea className="textarea" value={smsText} onChange={(e) => setSmsText(e.target.value)} placeholder={t('Vemid: Vaša poruka ovde… Odjava: STOP', 'Vemid: Your message here… Unsubscribe: STOP')} />
                <div className="flex items-center justify-between mt-8">
                  <span className="hint" style={{ margin: 0 }}>{t('Promenljive:', 'Variables:')} {'{{name}}'} · GSM-7</span>
                  <span className="chip"><span className="tnum">{smsText.length}</span> / 160 · {Math.max(1, Math.ceil((smsText.length || 1) / 160))} SMS</span>
                </div>
              </div>
            )}

            {channel === 'viber' && (
              <>
                <ViberMessageBuilder viberContent={viberContent} onChange={setViberContent} />
                <div style={{ marginTop: 16 }}>
                  <FallbackSelector
                    fallbackEnabled={fallbackEnabled}
                    fallbackSmsContent={fallbackSmsContent}
                    onChange={(enabled, content) => {
                      setFallbackEnabled(enabled);
                      setFallbackSmsContent(content);
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* live phone preview for sms/viber */}
          {channel !== 'email' && (
            <div className="wiz-preview" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', position: 'sticky', top: 88 }}>
              {channel === 'viber' ? (
                <ViberMessagePreview
                  viberContent={viberContent}
                  recipientData={{ name: 'Marko Marković', phoneNumber: '+381641234567', customData: {} }}
                />
              ) : (
                <SmsPhonePreview body={smsText} />
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 2 — audience */}
      {step === 2 && (
        <div className="wiz-audience">
          <div className="card">
            <div className="card-pad flex items-center justify-between" style={{ paddingBottom: 14 }}>
              <div className="card-title">{t('Izaberi liste', 'Choose lists')}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/contact-lists')}>
                <Icon name="plus" size={14} /> {t('Nova lista', 'New list')}
              </button>
            </div>
            {/* search + clear — selection persists across search & pagination */}
            <div className="flex items-center gap-8 wrap" style={{ padding: '0 16px 10px' }}>
              <div className="input-icon grow">
                <Icon name="search" size={16} />
                <input
                  className="input"
                  placeholder={t('Pretraži liste po imenu…', 'Search lists by name…')}
                  value={listSearch}
                  onChange={(e) => { setListSearch(e.target.value); setListPage(1); }}
                />
              </div>
              {selectedLists.length > 0 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedLists([])}>
                  <Icon name="x" size={14} /> {t('Poništi izbor', 'Clear')} ({selectedLists.length})
                </button>
              )}
            </div>
            <div style={{ padding: '0 10px 10px' }}>
              {availableLists.length === 0 ? (
                <div className="muted" style={{ padding: 20, fontSize: 14 }}>{t('Nema dostupnih lista. Kreiraj prvu pod „Liste".', 'No available lists. Create your first one under "Lists".')}</div>
              ) : filteredLists.length === 0 ? (
                <div className="muted" style={{ padding: 20, fontSize: 14 }}>{t('Nema lista za tu pretragu.', 'No lists match your search.')}</div>
              ) : (
                pagedLists.map((l) => (
                  <label key={l.id} className="lrow" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" className="row-check" checked={selectedLists.includes(l.id)} onChange={() => toggleList(l.id)} />
                    <span style={{ width: 38, height: 38, borderRadius: 'var(--r-sm)', background: 'var(--brand-50)', color: 'var(--brand-600)', display: 'grid', placeItems: 'center', flex: 'none' }}>
                      <Icon name="lists" size={18} />
                    </span>
                    <div className="grow">
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{l.name}</div>
                      <div className="dim" style={{ fontSize: 12.5 }}>{l.description || '—'}</div>
                    </div>
                    <span className="tnum" style={{ fontWeight: 700 }}>{fmt(l.contactCount || 0)}</span>
                  </label>
                ))
              )}
              <Pagination currentPage={listPageSafe} totalPages={listTotalPages} onPageChange={setListPage} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
            <div className="card card-pad" style={{ textAlign: 'center' }}>
              <div className="eyebrow">{t('Ukupan doseg', 'Total reach')}</div>
              <div className="tnum" style={{ fontSize: 42, fontWeight: 800, color: 'var(--brand-600)', margin: '6px 0' }}>{fmt(reach)}</div>
              <div className="muted" style={{ fontSize: 13 }}>{selectedLists.length} {t('lista izabrano', 'lists selected')}</div>
            </div>
            <div className="card card-pad">
              <div className="flex items-center gap-8" style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>
                <Icon name="info" size={16} style={{ color: 'var(--ch-sms)' }} /> {t('Deduplikacija', 'Deduplication')}
              </div>
              <p className="muted" style={{ fontSize: 13 }}>{t('Kontakti u više lista šalju se samo jednom. Odjavljeni se automatski izuzimaju', 'Contacts on multiple lists are sent only once. Unsubscribed contacts are automatically excluded')}{channel !== 'email' ? t(', a za SMS/Viber primaoci bez broja telefona se preskaču', ', and for SMS/Viber recipients without a phone number are skipped') : ''}.</p>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3 — review + schedule */}
      {step === 3 && channel && (
        <div className="wiz-review">
          <div className="card card-pad">
            <div className="card-title" style={{ marginBottom: 16 }}>{t('Pregled kampanje', 'Campaign review')}</div>
            {[
              [t('Naziv', 'Name'), name || '—'],
              [t('Kanal', 'Channel'), CHANNEL_META[channel].label],
              channel === 'email' ? [t('Subject', 'Subject'), subject || '—'] : null,
              [t('Primaoci', 'Recipients'), fmt(reach)],
              [t('Liste', 'Lists'), String(selectedLists.length)],
              channel === 'viber' ? [t('SMS rezerva', 'SMS fallback'), fallbackEnabled ? t('Uključeno', 'Enabled') : t('Isključeno', 'Disabled')] : null,
            ]
              .filter(Boolean)
              .map((r, i) => {
                const row = r as [string, string];
                return (
                  <div key={i} className="flex items-center justify-between" style={{ padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="muted" style={{ fontSize: 13.5 }}>{row[0]}</span>
                    <span style={{ fontWeight: 700, fontSize: 13.5 }}>{row[1]}</span>
                  </div>
                );
              })}
            <div style={{ marginTop: 16, padding: 14, background: 'var(--brand-50)', border: '1px solid var(--brand-200)', borderRadius: 'var(--r-md)', display: 'flex', gap: 10 }}>
              <Icon name="checkCircle" size={18} style={{ color: 'var(--brand-600)', flex: 'none', marginTop: 1 }} />
              <span style={{ fontSize: 13, color: 'var(--brand-800)', fontWeight: 600 }}>{t('Spremno. Pošalji probnu poruku sebi pre pokretanja (na detalju kampanje).', 'Ready. Send a test message to yourself before launching (on the campaign detail page).')}</span>
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-title" style={{ marginBottom: 16 }}>{t('Zakazivanje', 'Scheduling')}</div>
            <div className="seg" style={{ width: '100%', marginBottom: 18 }}>
              <button className={when === 'now' ? 'active' : ''} style={{ flex: 1 }} onClick={() => setWhen('now')}>
                <Icon name="zap" size={14} /> {t('Pošalji odmah', 'Send now')}
              </button>
              <button className={when === 'later' ? 'active' : ''} style={{ flex: 1 }} onClick={() => setWhen('later')}>
                <Icon name="clock" size={14} /> {t('Zakaži', 'Schedule')}
              </button>
            </div>
            {when === 'later' ? (
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label">{t('Datum i vreme', 'Date and time')}</label>
                <input className="input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                <div className="hint">{t('Vremenska zona: Beograd (CET).', 'Time zone: Belgrade (CET).')}</div>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: 13.5 }}>
                {t('Kampanja kreće odmah po potvrdi. Procena za', 'The campaign starts immediately after confirmation. Estimate for')} <b className="tnum">{fmt(reach)}</b> {t('primalaca: ≈', 'recipients: ≈')} {Math.max(1, Math.round(reach / 14 / 60))} min.
              </p>
            )}
          </div>
        </div>
      )}

      {/* footer nav */}
      <div style={{ marginTop: 28 }}>{navButtons}</div>
    </div>
  );
};

/** Minimal SMS phone mockup for the live preview. */
const SmsPhonePreview: React.FC<{ body: string }> = ({ body }) => {
  const t = useT();
  return (
  <div className="phone">
    <div className="phone-screen">
      <div className="phone-status"><span>9:41</span><span>SMS</span></div>
      <div style={{ background: 'var(--ch-sms)', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,.25)', display: 'grid', placeItems: 'center' }}>
          <Icon name="sms" size={15} />
        </span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Vemid</div>
          <div style={{ fontSize: 10.5, opacity: 0.85 }}>{t('Pošiljalac', 'Sender')}</div>
        </div>
      </div>
      <div style={{ flex: 1, padding: 14, overflow: 'auto' }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: 12, fontSize: 13, lineHeight: 1.5, boxShadow: '0 1px 2px rgba(0,0,0,.08)', color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {body || t('Vaša SMS poruka…', 'Your SMS message…')}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, textAlign: 'right' }}>9:41</div>
      </div>
    </div>
  </div>
  );
};

export default CampaignWizard;
